import createClient, { type Middleware } from "openapi-fetch";
import { env } from "@/shared/config/env";
import { openUpgradePrompt } from "@/shared/lib/upgradePrompt";
import type { paths } from "./contract";

/**
 * The typed API client — generated `paths` give full request/response typing.
 * Feature `api/` fns are the only callers; nothing else fetches.
 *
 * Auth: Clerk's session-token getter is injected once at app init via
 * `setTokenGetter` (see app/AuthBridge) and applied per-request by middleware,
 * so the client stays decoupled from React/Clerk.
 */
type TokenGetter = (opts?: { skipCache?: boolean }) => Promise<string | null>;

let getToken: TokenGetter = async () => null;

export function setTokenGetter(fn: TokenGetter): void {
  getToken = fn;
}

/**
 * The current Clerk session token, for callers outside `openapi-fetch` — namely
 * the hand-written SSE client (`sse.ts`), which can't go through the middleware
 * stack. Pass `{ skipCache: true }` to force a refresh (used to recover from a
 * 401). Returns null when signed out.
 */
export function getAuthToken(opts?: { skipCache?: boolean }): Promise<string | null> {
  return getToken(opts);
}

/**
 * Called when the API rejects our token (401). Wired by AuthBridge to refresh
 * the Clerk session and sign the user out (→ redirect to sign-in) if it's gone.
 */
let authErrorHandler: () => void = () => {};

export function setAuthErrorHandler(fn: () => void): void {
  authErrorHandler = fn;
}

/** Trigger the registered 401 recovery (token refresh / redirect to sign-in). */
export function notifyAuthError(): void {
  authErrorHandler();
}

/**
 * The current UI language (BCP-47), injected once at app init via
 * `setLanguageGetter` (see app/AuthBridge) so the client stays decoupled from
 * i18next. Sent as `Accept-Language` so the backend can localize responses
 * (e.g. model/connector descriptions). Defaults to English.
 */
let getLanguage: () => string = () => "en";

export function setLanguageGetter(fn: () => string): void {
  getLanguage = fn;
}

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = await getToken();
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    return request;
  },
  async onResponse({ response }) {
    // A rejected token → kick off recovery (refresh, or redirect to sign-in).
    if (response.status === 401) notifyAuthError();
    return undefined;
  },
};

/**
 * Global quota guard: when the backend answers `402 quota_exceeded` on ANY
 * request, open the shared upgrade prompt. Features just let the error bubble —
 * the modal is handled centrally here. The response body is read from a clone
 * so the original stream stays intact for the caller.
 */
const quotaMiddleware: Middleware = {
  async onResponse({ response }) {
    if (response.status === 402) {
      const body = (await response
        .clone()
        .json()
        .catch(() => null)) as { error?: { code?: string } } | null;
      if (body?.error?.code === "quota_exceeded") openUpgradePrompt();
    }
    return undefined;
  },
};

/** Tell the backend the user's language so it can localize responses. */
const languageMiddleware: Middleware = {
  async onRequest({ request }) {
    request.headers.set("Accept-Language", getLanguage());
    return request;
  },
};

export const api = createClient<paths>({
  baseUrl: env.API_BASE_URL,
  // Resolve `fetch` per call (not bound at creation) so the global stays the one
  // source of truth — runtime uses the platform fetch, tests can stub it.
  fetch: (request) => fetch(request),
});
api.use(authMiddleware);
api.use(languageMiddleware);
api.use(quotaMiddleware);
